(ns admin.events.containers.curriculum.report.learner
  "Responsible for bootstrapping the curriculum learner report page."
  (:require [kyber.router :refer [ensure-resource-v2]]
            [kyber.events.eql :as eql]
            [kyber.events.utils :refer [kyber-event-fx]]
            [re-frame.core :as re-frame]
            [kyber.lib.kyber-db :as kyber-db]
            [kyber.interceptors.cache-request :refer [cache-request]]
            [kyber.lib.error-reporting :as ke]
            [kyber.kyber-db.report-learner-data :as report-learner-data]))

(when ^boolean js/goog.DEBUG
  (def event-doc-fetch-curriculum-data-success
    {:description "Get learner report page (Success)"
     :changelog
     [{:date "29NOV21"
       :event-version 0
       :data {}}]}))

(defn fetch-curriculum-data-success
  [{:keys [db]} [event-name _params res]]
  {:db (kyber-db/tx-record db {:model :curriculum} (:>/curriculum res))
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::fetch-curriculum-data-success fetch-curriculum-data-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-fetch-curriculum-data-failure
    {:description "Get learner report page (Failure)"
     :changelog
     [{:date "29NOV21"
       :event-version 0
       :data {}}]}))

(defn fetch-curriculum-data-failure
  [{:keys [db]} [event-name _ _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::fetch-curriculum-data-failure fetch-curriculum-data-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-fetch-curriculum-data
    {:description "Get learner report page"
     :changelog
     [{:date "29NOV21"
       :event-version 0
       :data {}}]}))

(defn fetch-curriculum-data
  [{:keys [db]} [event-name {:keys [curriculum-uuid] :as params}]]
  {:db (cond-> db
         (some? curriculum-uuid)
         (kyber-db/retract-record {:model :curriculum} {:curriculum/uuid curriculum-uuid}))
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request
   (eql/request
    db
    {:name event-name
     :params `[{(:>/curriculum {:curriculum/uuid ~curriculum-uuid})
                [:curriculum/uuid
                 :curriculum/name
                 {(:curriculum/activities
                   {:include-unpublished? ~false})
                   [:activity/uuid
                    :activity/name
                    :activity/plo-type]}]}]
     :on-success [::fetch-curriculum-data-success params]
     :on-failure [::fetch-curriculum-data-failure params]})})

(kyber-event-fx ::fetch-curriculum-data fetch-curriculum-data)

(def ensure-curriculum-data
  (partial ensure-resource-v2
           ::fetch-curriculum-data
           #(re-frame/dispatch [::fetch-curriculum-data %])))

(when ^boolean js/goog.DEBUG
  (def event-doc-fetch-container-data-success
    {:description "Get container page success"
     :changelog
     [{:date "21JAN21"
       :event-version 0
       :data {}}]}))

(when ^boolean js/goog.DEBUG
  (def event-doc-fetch-container-data
    {:description "Get container page request"
     :changelog
     [{:date "21JAN21"
       :event-version 0
       :data {}}]}))

(def ^:private question-response-session-sub-query
  [:question.response/confidence
   :question.response/correct?

   {:question.response/value [:question.choice/index
                              :question.header/index]}
   :question.response/status])

(def ^:private moment-question-session-sub-query
  {'(:activity/moments {:type "question"})
   (into [:moment/uuid
          :moment/halo-consumed]
         question-response-session-sub-query)})

(def ^:private learning-objective-session-sub-query
  {:activity/learning-objectives
   [:learning-objective/uuid
    #_:learning-objective/messages
    :learning-objective/readiness
    :learning-objective/readiness-label
    :learning-objective/first-pre-test-correct-count
    :learning-objective/first-post-test-correct-count
    :learning-objective/first-pre-test-count
    :learning-objective/first-post-test-count]})

(def ^:private test-attempt-sub-query
  [{:questionnaire.attempt/questions
    (into [:question/uuid] question-response-session-sub-query)}
   :questionnaire.attempt/submitted-at])

(def ^:private post-test-sub-query
  {:activity-session/post-test
   [{:questionnaire.attempts/first test-attempt-sub-query}
    {:questionnaire.attempts/last test-attempt-sub-query}
    :questionnaire.attempts/count]})

(def ^:private pre-test-sub-query
  {:activity-session/pre-test
   [{:questionnaire.attempts/first test-attempt-sub-query}]})

(def ^:private resource-sub-query
  [:resource/uuid
   :resource/file-type
   :resource/file-size
   :resource/name
   :resource/description
   :resource/url
   :resource/status
   :resource/m3u8
   :resource/viewer-path
   :resource/download-path
   :resource/source-path
   :resource/duration
   :resource/page-count])

(def ^:private question-sub-query
  [:question/uuid
   :question/body
   :question/key
   :question/has-correctness
   :question/type
   :question/other-enabled
   :question/results
   :question/educator-feedback
   {:question/choices [:question.choice/uuid
                       :question.choice/body
                       :question.choice/correct?
                       :question.choice/index]}
   {:question/headers [:question.header/uuid
                       :question.header/body
                       :question.header/index]}
   {:question/correct-answer [:question.choice/index]}])

(def ^:private questionnaire-question-sub-query
  [:question.context/uuid
   {:question.context/question question-sub-query}
   {:question.context/resources resource-sub-query}
   {:question.context/learning-objective [:learning-objective/uuid]}
   ;; Aggregation Data
   :question.aggregates/percent-correct
   {:question.aggregates/responses [{:question.aggregates.response/choice [:question.choice/uuid]}
                                    :question.aggregates.response/sure-count
                                    :question.aggregates.response/guess-count
                                    :question.aggregates.response/unsure-count
                                    :question.aggregates.response/no-confidence-count]}])

(def ^:private activity-report-sub-query
  [:activity/uuid
   :activity/name
   :activity/plo-type
   {:activity/post-test [:questionnaire/uuid
                         {:assessment/questions questionnaire-question-sub-query}]}
   {'(:activity/moments {:type "question"})
    [:moment/uuid
     :moment/type
     {:moment/resources resource-sub-query}
     {:moment/learning-objective [:learning-objective/uuid]}
     {:moment/question question-sub-query}
     ;; Moment Aggregation Data
     :question.aggregates/percent-correct
     {:question.aggregates/responses [{:question.aggregates.response/choice [:question.choice/uuid]}
                                      :question.aggregates.response/sure-count
                                      :question.aggregates.response/guess-count
                                      :question.aggregates.response/unsure-count
                                      :question.aggregates.response/no-confidence-count]}]}
   {:activity/learning-objectives
    [:learning-objective/uuid
     :learning-objective/ordinal
     :learning-objective/label
     :learning-objective/description
     :learning-objective/average-readiness
     :learning-objective/average-first-pre-test-correct-count
     :learning-objective/average-first-post-test-correct-count]}])

(def ^:private sub-query-learner
  `[:user/given-name
    :user/family-name
    :user/email-address
    :user/uuid
    :activity/uuid
    :activity-session/started-at
    :activity-session/mindset
    :activity-session/readiness
    :activity-session/readiness-label
    :activity-session/lower-quartile-readiness
    :activity-session/upper-quartile-readiness
    :activity-session/low-mindset-count
    :activity-session/medium-mindset-count
    :activity-session/high-mindset-count
    ~moment-question-session-sub-query
    ~learning-objective-session-sub-query
    ~post-test-sub-query
    ~pre-test-sub-query])

(defn fetch-learner-container-data-success
  [{:keys [db]} [event-name _ {:>/keys [learner]}]]
  {:db (kyber-db/tx-record db {:model :report-learner-data} learner)
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::fetch-learner-container-data-success fetch-learner-container-data-success)

(defn fetch-learner-container-data-failure
  [{:keys [db]} [event-name params err]]
  {:db db
   :dispatch-n [[:report-error (ke/->failed-request-error event-name (assoc params :error err))]
                [:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::fetch-learner-container-data-failure fetch-learner-container-data-failure)

(defn- learner-report-cache
  [{:keys [db]} [_ {:keys [activity-uuid learner-uuid]}]]
  (some? (kyber-db/get-record db :report-learner-data
                              (report-learner-data/->ident-key
                               {:activity/uuid activity-uuid
                                :user/uuid learner-uuid}))))

(defn fetch-learner-container-data
  [{:keys [db cached?]}
   [event-name {:keys [activity-uuid learner-uuid] :as params}]]
  (cond-> {:db db
           :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                    :event-version 0}]]}
    (false? cached?)
    (assoc :request
           (eql/request
            db
            {:name event-name
             :params `[{(:>/learner {:activity/uuid ~activity-uuid
                                     :user/uuid ~learner-uuid})
                        ~sub-query-learner}]
             :on-success [::fetch-learner-container-data-success params]
             :on-failure [::fetch-learner-container-data-failure params]}))))

(kyber-event-fx ::fetch-learner-container-data
                [(cache-request learner-report-cache)]
                fetch-learner-container-data)

(defn fetch-activity-report-metadata-success
  [{:keys [db]} [event-name _ {:>/keys [activity]}]]
  {:db (kyber-db/tx-record db {:model :activity} (assoc activity :activity/contains-report-data? true))
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::fetch-activity-report-metadata-success fetch-activity-report-metadata-success)

(defn fetch-activity-report-metadata-failure
  [{:keys [db]} [event-name params err]]
  {:db db
   :dispatch-n [[:report-error (ke/->failed-request-error event-name (assoc params :error err))]
                [:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::fetch-activity-report-metadata-failure fetch-activity-report-metadata-failure)

(defn- activity-report-metadata-cache
  [{:keys [db]} [_ {:keys [activity-uuid]}]]
  (let [{:activity/keys [contains-report-data?]} (kyber-db/get-record db :activity activity-uuid)]
    (true? contains-report-data?)))

(defn fetch-activity-report-metadata
  [{:keys [db cached?]}
   [event-name {:keys [activity-uuid learner-uuid] :as params}]]
  (cond-> {:db db
           :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                    :event-version 0}]]}
    (false? cached?)
    (assoc :request
           (eql/request
            db
            {:name event-name
             :params `[{(:>/activity {:activity/uuid ~activity-uuid
                                      :user/uuid ~learner-uuid})
                        ~activity-report-sub-query}]
             :on-success [::fetch-activity-report-metadata-success params]
             :on-failure [::fetch-activity-report-metadata-failure params]}))))

(kyber-event-fx ::fetch-activity-report-metadata
                [(cache-request activity-report-metadata-cache)]
                fetch-activity-report-metadata)

(def ensure-learner-report
  "Fetches data for the learner report curriculum index page"
  (partial ensure-resource-v2
           (fn [{{:strs [activity-uuid learner-uuid]} :route-query}]
             (keyword (namespace ::this)
                      (str "fetch-learner-container-data_"
                           activity-uuid "_" learner-uuid)))
           (fn [route-params {:strs [activity-uuid learner-uuid]}]
             (let [params (cond-> route-params
                            (nil? (:activity-uuid route-params))
                            (assoc :activity-uuid activity-uuid)
                            (nil? (:learner-uuid route-params))
                            (assoc :learner-uuid learner-uuid))]
               (re-frame/dispatch [::fetch-activity-report-metadata params])
               (re-frame/dispatch [::fetch-learner-container-data params])))))
